/**
 * MainRecommendations Component
 * Displays the first two recommendations as large cards in a row
 * Mobile-first design optimized for iPhone SE (375px width)
 */

import { Plus } from 'lucide-react';
import type { Recommendation } from '../../../types/agenticChat';
import type { Language } from './VoiceOrderingContainer';
import { formatPrice, getItemName } from './recommendationHelpers';

interface MainRecommendationsProps {
  recommendations: Recommendation[];
  onItemClick: (item: Recommendation) => void;
  onAddToOrder: (item: Recommendation) => void;
  language: Language;
  dismissedIndices: Set<number>;
  disabled?: boolean; // Disable interactivity when AI is processing
}

export function MainRecommendations({
  recommendations,
  onItemClick,
  onAddToOrder,
  language,
  dismissedIndices,
  disabled = false,
}: MainRecommendationsProps) {
  // Get first 2 recommendations that aren't dismissed, keeping track of original indices
  const activeMainRecommendations = recommendations
    .slice(0, 2)
    .map((item, index) => ({ item, originalIndex: index }))
    .filter(({ originalIndex }) => !dismissedIndices.has(originalIndex));

  if (activeMainRecommendations.length === 0) {
    return null;
  }

  return (
    <div className="w-full px-4 py-1">
      <div className="mx-auto flex max-w-4xl gap-3 justify-center">
        {activeMainRecommendations.map(({ item, originalIndex }) => {

          const itemName = getItemName(item.item, language);
          const displayPrice = formatPrice(item.price, language);

          return (
            <div
              key={originalIndex}
              className={`flex-shrink-0 rounded-xl overflow-hidden shadow-lg transition-all relative z-0 ${disabled ? '' : 'hover:shadow-xl active:scale-[0.98]'}`}
              style={{
                width: 'calc(48% - 6px)', // ~48% width with gap, mobile-first
                minWidth: '160px', // Ensure minimum width on very small screens
                background: 'var(--card-bg)',
                border: '1px solid var(--border)',
                opacity: disabled ? 0.5 : 1, // Visual feedback when disabled
                pointerEvents: disabled ? 'none' : 'auto', // Disable interactions when processing
              }}
            >
              {/* Clickable Image Section */}
              <div
                className={disabled ? 'relative cursor-not-allowed' : 'relative cursor-pointer'}
                style={{ height: '120px' }}
                onClick={() => !disabled && onItemClick(item)}
              >
                {item.img_path ? (
                  <img
                    src={item.img_path}
                    alt={itemName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.innerHTML =
                          '<div style="display: flex; align-items: center; justify-content: center; height: 100%; width: 100%; font-size: 3rem; background: var(--cream-medium);">🍽️</div>';
                      }
                    }}
                  />
                ) : (
                  <div
                    className="flex h-full w-full items-center justify-center text-5xl"
                    style={{ background: 'var(--cream-medium)' }}
                  >
                    🍽️
                  </div>
                )}
              </div>

              {/* Card Content */}
              <div className="p-2" style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                {/* Title */}
                <h3
                  className="text-sm font-semibold mb-2 line-clamp-2"
                  style={{
                    color: 'var(--text-primary)',
                    minHeight: '2.5rem', // Ensure consistent height for 2 lines
                  }}
                  title={itemName}
                >
                  {itemName}
                </h3>

                {/* Price and Add Button Row */}
                <div className="flex items-center justify-between gap-2">
                  {/* Price */}
                  <p
                    className="text-base font-bold flex-shrink-0"
                    style={{ color: 'var(--accent-gold)' }}
                  >
                    {displayPrice}
                  </p>

                  {/* Add to Order Button */}
                  <button
                    onClick={(e) => {
                      if (!disabled) {
                        e.stopPropagation();
                        onAddToOrder(item);
                      }
                    }}
                    disabled={disabled}
                    className={`flex-shrink-0 flex items-center justify-center rounded-full transition-all ${disabled ? 'cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}
                    style={{
                      width: '44px',
                      height: '44px',
                      background: 'var(--accent-gold)',
                      color: 'white',
                      opacity: disabled ? 0.5 : 1,
                    }}
                    aria-label={language === 'en' ? 'Add to order' : 'أضف إلى الطلب'}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
